import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { Logo } from "./Logo";
import { LayoutDashboard, GraduationCap, Settings, LogOut, Crown, Wifi, WifiOff, FileBarChart2, Building2, MessageSquareText, Siren, Languages, TreeDeciduous, BookOpen, GitFork, MessagesSquare, Video, BookOpenCheck, Globe2, Gamepad2, FlaskConical, Clapperboard, ShieldCheck, ShoppingBag } from "lucide-react";
import { BaobabOnboarding } from "./baobab/BaobabOnboarding";
import { CountryOnboarding } from "./CountryPicker";
import { ResilienceBanner, useCentre } from "./baobab/CentreHeader";

const items = [
  { to: "/pro", end: true, icon: LayoutDashboard, label: "Tableau de bord", testid: "pro-nav-dashboard" },
  { to: "/pro/reseau", icon: TreeDeciduous, label: "Réseau", testid: "pro-nav-network" },
  { to: "/pro/bibliotheque", icon: BookOpen, label: "Bibliothèque", testid: "pro-nav-library" },
  { to: "/pro/transmission", icon: GitFork, label: "Transmission", testid: "pro-nav-transmission" },
  { to: "/pro/palabre", icon: MessagesSquare, label: "Palabre", testid: "pro-nav-palabre", badge: "palabre" },
  { to: "/pro/telesoin", icon: Video, label: "Télésoin", testid: "pro-nav-telesoin", badge: "telesoin" },
  { to: "/pro/jeu", icon: Gamepad2, label: "Recovery-Explorer", testid: "pro-nav-jeu" },
  { to: "/pro/etude", icon: FlaskConical, label: "Étude clinique", testid: "pro-nav-etude" },
  { to: "/boutique", icon: ShoppingBag, label: "Boutique", testid: "pro-nav-boutique" },
  { to: "/pro/videos", icon: Clapperboard, label: "Vidéos", testid: "pro-nav-videos", racine: true },
  { to: "/pro/admin", icon: ShieldCheck, label: "Administration", testid: "pro-nav-admin", racine: true },
  { to: "/pro/nomenclature", icon: BookOpenCheck, label: "Nomenclature", testid: "pro-nav-nomenclature" },
  { to: "/pro/pays", icon: Globe2, label: "Réseau pays", testid: "pro-nav-pays", racine: true },
  { to: "/pro/inquietudes", icon: Siren, label: "Inquiétudes", testid: "pro-nav-worries", badge: "worries" },
  { to: "/pro/reports", icon: FileBarChart2, label: "Rapports", testid: "pro-nav-reports" },
  { to: "/pro/transcriptions", icon: MessageSquareText, label: "Transcriptions", testid: "pro-nav-transcriptions" },
  { to: "/pro/clinic", icon: Building2, label: "Clinique", testid: "pro-nav-clinic" },
  { to: "/pro/training", icon: GraduationCap, label: "Formation", testid: "pro-nav-training" },
  { to: "/pro/lingala", icon: Languages, label: "Lingala", testid: "pro-nav-lingala", feature: "lingala" },
  { to: "/pro/subscription", icon: Crown, label: "Forfait", testid: "pro-nav-subscription" },
  { to: "/pro/settings", icon: Settings, label: "Réglages", testid: "pro-nav-settings" },
];

export default function ProLayout() {
  const { user, logout } = useAuth();
  const { online, billing, profile } = useApp();
  const visible = items.filter((i) => (!i.feature || !profile || profile.features?.includes(i.feature)) && (!i.racine || profile?.est_racine));
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/auth/login", { replace: true }); };
  const [worries, setWorries] = useState(0);
  const me = useCentre();
  const [onb, setOnb] = useState(() => !localStorage.getItem("bc_baobab_onboarding"));
  const showOnb = onb && user?.consent?.accepte && !user.onboarding_baobab_vu;
  const [palabre, setPalabre] = useState(0);
  const [teleAlert, setTeleAlert] = useState(false);
  useEffect(() => {
    const load = () => { api.get("/therapist/worries/count").then(({ data }) => setWorries(data.ouverts)).catch(() => null); api.get("/palabre/unread").then(({ data }) => setPalabre(data.non_lus)).catch(() => null); api.get("/telesoin/compteur").then(({ data }) => setTeleAlert(data.applicable && ["alerte", "bloque"].includes(data.niveau))).catch(() => null); };
    load();
    window.addEventListener("bc-worries-changed", load);
    const t = setInterval(load, 120000);
    return () => { window.removeEventListener("bc-worries-changed", load); clearInterval(t); };
  }, []);
  const Badge = ({ k, mobile }) => {
    const n = k === "worries" ? worries : k === "palabre" ? palabre : k === "telesoin" ? (teleAlert ? "!" : 0) : 0;
    if (!n) return null;
    return <span data-testid={`${k}-nav-badge${mobile ? "-mobile" : ""}`} className={`${mobile ? "absolute -top-1 right-2" : "ml-auto"} min-w-[20px] h-5 px-1.5 rounded-full ${k === "worries" || k === "telesoin" ? "bg-[#D9534F] text-white" : "bg-[#D4AF5A] text-[#0A1426]"} text-[10px] font-bold grid place-items-center`}>{n}</span>;
  };

  return (
    <div className="min-h-dvh bg-[#F5F3EE] flex" data-testid="pro-shell">
      {showOnb && <BaobabOnboarding me={me} onDone={() => setOnb(false)} />}
      <CountryOnboarding />
        <footer data-testid="bombacare-footer" className="text-center text-[10px] text-white/40 py-3">© Bombacare, tous droits réservés · Outil d'accompagnement, ne remplace pas une consultation.</footer>
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-[#0A1426] text-white p-5 sticky top-0 h-dvh">
        <div className="flex items-center gap-3 mb-8">
          <Logo size={40} />
          <div>
            <div className="font-[Fraunces] text-lg font-bold leading-none">Bombacare</div>
            <div data-testid="pro-subtitle" className="text-[11px] text-white/60 mt-0.5">Mon Centre Bombacare</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {visible.map(({ to, end, icon: Icon, label, testid, badge }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-colors ${isActive ? "bg-[#2BA3A0] text-white" : "text-white/70 hover:bg-white/10"}`}>
              <Icon size={18} /> {label} <Badge k={badge} />
            </NavLink>
          ))}
        </nav>
        <div className="mb-3"><ResilienceBanner compact /></div>
        <div className="rounded-2xl bg-white/10 p-3 mb-3">
          <div className="text-[11px] text-white/60">Connecté</div>
          <div className="font-bold text-sm">{user?.nom}</div>
          <div className="text-[11px] text-[#D4AF5A] font-bold mt-1">{billing?.plan_name || "Essai"}</div>
        </div>
        <button data-testid="pro-logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-white/70 hover:text-white px-3 py-2">
          <LogOut size={16} /> Se déconnecter
        </button>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <div data-testid="pro-conn-banner" className={`px-4 py-1.5 text-[11px] font-semibold flex items-center gap-2 ${online ? "bg-[#0A1426] text-white md:bg-transparent md:text-[#0A1426]/60" : "bg-[#D4AF5A] text-[#0A1426]"}`}>
          {online ? <Wifi size={12} /> : <WifiOff size={12} />} {online ? "En ligne — à jour" : "Hors ligne — lecture seule"}
        </div>
        <main className="flex-1 px-4 md:px-8 pb-24 md:pb-10 pt-2 max-w-6xl w-full">
          <Outlet />
        </main>
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E4E0D5] flex overflow-x-auto px-2 pt-2 pb-safe z-40" style={{ scrollbarWidth: "none" }}>
          {visible.map(({ to, end, icon: Icon, label, testid, badge }) => (
            <NavLink key={to} to={to} end={end} data-testid={`${testid}-mobile`}
              className={({ isActive }) => `relative flex flex-col items-center gap-1 py-1.5 min-w-[56px] ${isActive ? "text-[#2BA3A0]" : "text-[#0A1426]/60"}`}>
              <Icon size={22} /><span className="text-[10px] font-bold">{label}</span><Badge k={badge} mobile />
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
